/**
 * Poll a ring-swap generation job to a terminal state. Extracted from TestBench verbatim so the
 * Generation History drawer can reuse the identical polling behaviour. Display-side callers pass no
 * `opts`; the exported defaults apply. Tests pass a tiny `{ intervalMs, maxTries }` to run in ms.
 */
import type { AuthService } from "../auth/auth-service";
import { pollJob } from "../api/ringswap";

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Mirrored from `packages/config` (`WORKER_LEASE_SECONDS`, `MAX_GENERATION_ATTEMPTS`). The lease TTL
 * is deliberately set "above p99 generation latency", and a job may be retried up to maxAttempts
 * times, so the longest a healthy job may legitimately take is lease × attempts. Hardcoded because
 * the SPA has no view of the worker's env; keep in step if those defaults change.
 */
const WORKER_LEASE_SECONDS = 120;
const MAX_GENERATION_ATTEMPTS = 3;

export const POLL_INTERVAL_MS = 2000;

/**
 * Watch for as long as the worker may legitimately take — the whole retry budget, not a guess.
 *
 * This used to be 60s (30 × 2s), which could not cover even ONE attempt's p99 (the 120s lease), let
 * alone a retry. Every slow-but-healthy generation therefore reported "timeout" while the job was
 * still alive and about to succeed — the same wrong-thing-to-debug signal this module already
 * refuses to send for `failed`. Nothing is lost when the window does elapse: the job keeps running,
 * its result is cached against the variant hash, and it appears in Generation History regardless.
 */
export const POLL_WINDOW_MS = WORKER_LEASE_SECONDS * MAX_GENERATION_ATTEMPTS * 1000; // 6 minutes
export const POLL_MAX_TRIES = Math.ceil(POLL_WINDOW_MS / POLL_INTERVAL_MS); // 180

/** Poll a job to a terminal state, or "timeout" — which means only that the bench stopped watching,
 * NOT that the job died. `failed` IS terminal — the worker marked the job failed (e.g. the model
 * rejected an input), and polling past it just fakes a timeout, which sends whoever is testing off
 * to debug the wrong thing. onTick reports poll progress for the UI. */
export async function pollToFinal(
  service: AuthService,
  jobId: string,
  onTick?: (attempt: number, elapsedSeconds: number) => void,
  opts?: { intervalMs?: number; maxTries?: number },
): Promise<{ status: string; url?: string | undefined }> {
  const intervalMs = opts?.intervalMs ?? POLL_INTERVAL_MS;
  const maxTries = opts?.maxTries ?? POLL_MAX_TRIES;
  for (let i = 0; i < maxTries; i++) {
    await sleep(intervalMs);
    onTick?.(i + 1, Math.round(((i + 1) * intervalMs) / 1000));
    const job = await pollJob(service, jobId);
    if (job.status === "complete" || job.status === "rejected" || job.status === "in_review" || job.status === "failed") {
      return job;
    }
  }
  return { status: "timeout" };
}
